import React, { useEffect, useRef, useState } from "react";
import {
  RadioGroup, FormControlLabel, Radio, TextField, Chip, CircularProgress, MenuItem, Paper,
} from "@material-ui/core";
import { searchAffiliates } from "./checkoutProductsApi";

// Value shape, as the API expects it:
//   { mode: "everyone" | "restricted", grants: [{ audienceType: "affiliate", audienceId }] }
// `labelCache` (audienceId -> display label) keeps chips reading as names rather
// than raw ids. Display-only, never sent to the API. Parent owns both.
export default function AudiencePicker({ value, onChange, labelCache, setLabelCache }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const mode = (value && value.mode) || "everyone";
  const grants = (value && value.grants) || [];
  const chosen = new Set(grants.map((g) => String(g.audienceId)));

  // Debounced search while in restricted mode.
  useEffect(() => {
    if (mode !== "restricted") return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        setResults(await searchAffiliates(query.trim()));
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, mode]);

  const setMode = (m) =>
    onChange(m === "restricted" ? { mode: "restricted", grants } : { mode: "everyone", grants: [] });

  const add = (aff) => {
    const id = String(aff._id);
    if (chosen.has(id)) return;
    setLabelCache({ ...labelCache, [id]: aff.name || aff.companyName || aff.email || id });
    onChange({ mode: "restricted", grants: [...grants, { audienceType: "affiliate", audienceId: id }] });
    setQuery("");
    setOpen(false);
  };

  const remove = (id) =>
    onChange({ mode: "restricted", grants: grants.filter((g) => String(g.audienceId) !== String(id)) });

  return (
    <div>
      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        <FormControlLabel value="everyone" control={<Radio color="primary" />} label="Everyone" />
        <FormControlLabel value="restricted" control={<Radio color="primary" />} label="Specific affiliates" />
      </RadioGroup>

      {mode === "restricted" && (
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {grants.map((g) => (
              <Chip
                key={g.audienceId}
                label={(labelCache && labelCache[g.audienceId]) || g.audienceId}
                onDelete={() => remove(g.audienceId)}
              />
            ))}
            {!grants.length && (
              <span style={{ color: "#c62828", fontSize: 13 }}>Add at least one affiliate.</span>
            )}
          </div>

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search affiliates by name, email, or id"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            InputProps={{ endAdornment: loading ? <CircularProgress size={16} /> : null }}
          />

          {open && results.length > 0 && (
            <Paper style={{ position: "absolute", zIndex: 20, left: 0, right: 0, maxHeight: 240, overflowY: "auto" }}>
              {results.map((aff) => (
                <MenuItem
                  key={aff._id}
                  disabled={chosen.has(String(aff._id))}
                  onClick={() => add(aff)}
                >
                  {(aff.name || aff.companyName || "Unnamed")}{aff.email ? ` · ${aff.email}` : ""}
                </MenuItem>
              ))}
            </Paper>
          )}
        </div>
      )}

      <div style={{ fontSize: 12, color: "#7b8a83", marginTop: 8 }}>
        Removing an affiliate hides this product from them and disables their live pages that use it.
      </div>
    </div>
  );
}
